import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { AxiosError } from "axios";
import api from "../../utils/api";
import { useDarkMode } from "../../context/ThemeContext";
import { brand } from "../../config/theme";
import { useDebounce } from "../../hooks/useDebounce";
import {
  useCompaniesStore,
  useDisableCRUD,
} from "../../store/admin/companiesStore";
import { CRUDTooltip } from "../../components/base/CRUDTooltip";
import { getThemeClasses } from "../admin/shared";
import PaginationControls from "../../components/base/PaginationControls";
import { getPapeleraEntity } from "./papeleraConfig";
import { usePapeleraPermissions } from "./usePapeleraPermissions";
import { archiveOrAlert } from "../../features/device-archive/jobs";
import type { ArchiveSource } from "../../features/device-archive/jobs";

const ALL_BACKUP_SOURCES: ArchiveSource[] = [
  "positions",
  "events",
  "commands",
  "geofences",
];
import { pickBackupSources } from "../../features/device-archive/backupModal";

const ITEMS_PER_PAGE = 20;
const DANGER = "#ef4444";

interface DeletedRow {
  id: string;
  [key: string]: unknown;
}

// Campos que no aportan al usuario en el detalle (ruido interno / ya mostrados).
const HIDDEN_FIELDS = new Set(["id", "collections", "status_id", "password"]);

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "✓" : "✗";
  if (typeof v === "number" || typeof v === "string") return String(v);
  if (Array.isArray(v)) return v.length ? `[${v.length}]` : "—";
  if (typeof v === "object") {
    const obj = v as Record<string, unknown>;
    const secs = (obj._seconds ?? obj.seconds) as number | undefined;
    if (typeof secs === "number") return new Date(secs * 1000).toLocaleString();
    try {
      return JSON.stringify(v);
    } catch {
      return "—";
    }
  }
  return String(v);
};

const primaryTitle = (row: DeletedRow, keys: string[]): string => {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.trim()) return v;
    if (typeof v === "number") return String(v);
  }
  return row.id;
};

export default function PapeleraSection() {
  const { entity: entityId } = useParams<{ entity: string }>();
  const entity = getPapeleraEntity(entityId);

  const { t } = useTranslation("common");
  const { isDarkMode } = useDarkMode();
  const theme = getThemeClasses(isDarkMode);
  const queryClient = useQueryClient();
  const permissions = usePapeleraPermissions();
  const disableCRUD = useDisableCRUD();

  const companyId = useCompaniesStore((s) => s.companyId);
  const fetchAllCompanies = useCompaniesStore((s) => s.fetchAllCompanies);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    setPage(1);
    setSearch("");
    setExpanded(new Set());
  }, [entityId]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, companyId]);

  const query = useQuery({
    queryKey: ["papelera", entityId, companyId, page, debouncedSearch],
    enabled: !!entity,
    queryFn: async () => {
      const res = await api.get(`/papelera/${entityId}`, {
        params: {
          page,
          limit: ITEMS_PER_PAGE,
          search: debouncedSearch || undefined,
        },
      });
      return res.data as {
        data: DeletedRow[];
        meta?: { totalPages?: number; totalItems?: number };
      };
    },
  });

  const rows = useMemo(() => query.data?.data ?? [], [query.data]);
  const totalPages = query.data?.meta?.totalPages || 1;
  const totalItems = query.data?.meta?.totalItems ?? rows.length;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["papelera"] });
    queryClient.invalidateQueries({ queryKey: ["counts"] });
    queryClient.invalidateQueries({ queryKey: ["resellerCounts"] });
    // Al restaurar una compañía debe reaparecer en el listado y en el selector
    // del header (store legacy allCompanies).
    if (entityId === "company") {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      fetchAllCompanies(true);
    }
    // Restaurar/borrar un patio cambia el conteo de la pantalla de patios, que
    // usa su propia query (no es parte de counts/resellerCounts).
    if (entityId === "yard") {
      queryClient.invalidateQueries({ queryKey: ["truckYardCounts"] });
    }
  };

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const errorMessage = (err: unknown, fallback: string): string => {
    if (err instanceof AxiosError) {
      const data = err.response?.data as
        | { message?: string; messageKey?: string }
        | undefined;
      const status = err.response?.status;
      if (status === 409) return t("papelera.errors.inUse");
      if (data?.messageKey === "truckYardGeofenceReadOnly")
        return t("papelera.errors.truckYardGeofence");
      if (status === 403) return t("papelera.errors.companyAccess");
      return data?.message || fallback;
    }
    return fallback;
  };

  const handleRestore = async (row: DeletedRow) => {
    if (!entity) return;
    const confirm = await Swal.fire({
      title: t("papelera.restore.confirmTitle"),
      text: t("papelera.restore.confirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: brand.primary,
      cancelButtonColor: "#6b7280",
      confirmButtonText: t("papelera.restore.confirmButton"),
      cancelButtonText: t("papelera.cancel"),
    });
    if (!confirm.isConfirmed) return;

    setBusyId(row.id);
    Swal.fire({
      title: t("papelera.restore.processing"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      await api.post(
        `/papelera/${entity.id}/restore`,
        {},
        { params: { id: row.id } },
      );
      invalidate();
      await Swal.fire({
        icon: "success",
        title: t("papelera.restore.successTitle"),
        confirmButtonColor: brand.primary,
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: t("papelera.errors.title"),
        text: errorMessage(err, t("papelera.restore.error")),
        confirmButtonColor: DANGER,
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleHardDelete = async (row: DeletedRow) => {
    if (!entity) return;

    if (entity.id === "company" || entity.id === "device") {
      const isDevice = entity.id === "device";
      const choice = await pickBackupSources("delete", {
        v5Note: !isDevice,
        entity: isDevice ? "device" : "company",
        side: "v5",
      });
      if (choice.action === "cancel") return;
      if (isDevice) {
        if (companyId) {
          const sources =
            choice.action === "archive" ? choice.sources : ALL_BACKUP_SOURCES;
          const exportCsv = choice.action === "archive";
          await archiveOrAlert(companyId, "delete", sources, [row.id], exportCsv);
        }
      } else if (choice.action === "archive") {
        await archiveOrAlert(row.id, "delete", choice.sources);
      }
    } else {
      const keyword = t("papelera.hardDelete.keyword");
      const confirm = await Swal.fire({
        title: t("papelera.hardDelete.confirmTitle"),
        html: t("papelera.hardDelete.confirmHtml", { keyword }),
        icon: "warning",
        input: "text",
        inputPlaceholder: keyword,
        showCancelButton: true,
        confirmButtonColor: DANGER,
        cancelButtonColor: "#6b7280",
        confirmButtonText: t("papelera.hardDelete.confirmButton"),
        cancelButtonText: t("papelera.cancel"),
        preConfirm: (value: string) => {
          if ((value || "").trim().toUpperCase() !== keyword.toUpperCase()) {
            Swal.showValidationMessage(t("papelera.hardDelete.mismatch"));
            return false;
          }
          return true;
        },
      });
      if (!confirm.isConfirmed) return;
    }

    setBusyId(row.id);
    Swal.fire({
      title: t("papelera.hardDelete.processing"),
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    try {
      await api.delete(`/hardDelete/${entity.id}`, {
        params: { [entity.hardDeleteIdParam]: row.id },
      });
      invalidate();
      await Swal.fire({
        icon: "success",
        title: t("papelera.hardDelete.successTitle"),
        confirmButtonColor: brand.primary,
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: t("papelera.errors.title"),
        text: errorMessage(err, t("papelera.hardDelete.error")),
        confirmButtonColor: DANGER,
      });
    } finally {
      setBusyId(null);
    }
  };

  if (!entity) {
    return (
      <div className={`h-full flex items-center justify-center ${theme.textSecondary}`}>
        {t("papelera.unknownEntity")}
      </div>
    );
  }

  if (!permissions.canManage(entity)) {
    return (
      <div className={`h-full flex flex-col items-center justify-center gap-2 ${theme.textSecondary}`}>
        <i className="ri-lock-2-line text-5xl" />
        <p>{t("papelera.noAccess")}</p>
      </div>
    );
  }

  const canManage = permissions.canManage(entity);

  // Las entidades sin scope de compañía (p.ej. companies) no requieren tener una
  // compañía seleccionada para restaurar, y no muestran el tooltip de "selecciona
  // una compañía".
  const guardByCompany = entity.companyScoped !== false;
  const actionDisabled = (rowId: string) =>
    busyId === rowId || (guardByCompany && disableCRUD);
  const renderGuard = (node: ReactNode) =>
    guardByCompany ? <CRUDTooltip>{node}</CRUDTooltip> : <>{node}</>;

  return (
    <div className="h-full flex flex-col min-w-0 overflow-x-hidden">
      {/* Header */}
      <div className={`border-b ${theme.border} p-3 sm:p-5`}>
        <div className="flex items-center gap-3 mb-3">
          <i className={`${entity.icon} text-2xl text-red-500`} />
          <div>
            <h1 className={`text-lg font-semibold ${theme.text}`}>
              {t(entity.labelKey)}
            </h1>
            <p className={`text-xs ${theme.textSecondary}`}>
              {t("papelera.subtitle", { count: totalItems })}
            </p>
          </div>
        </div>
        <div className="relative max-w-sm">
          <i
            className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${theme.textSecondary}`}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("papelera.searchPlaceholder")}
            className={`w-full pl-9 pr-3 py-2 rounded-lg text-sm border ${theme.border} ${theme.input} ${theme.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
          />
        </div>
        {entity.id === "device" && (
          <div className="mt-3 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40">
            <i className="ri-error-warning-line text-amber-500 text-base shrink-0" />
            <span className="text-xs text-amber-700 dark:text-amber-300 text-center">
              {t("papelera.hardDelete.deviceCascadeBadge")}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-5">
        {query.isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
          </div>
        ) : query.isError ? (
          <div className={`flex flex-col items-center justify-center py-16 ${theme.textSecondary}`}>
            <i className="ri-error-warning-line text-5xl mb-3" />
            <p>{t("papelera.loadError")}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-16 ${theme.textSecondary}`}>
            <i className="ri-delete-bin-7-line text-5xl mb-3" />
            <p>{t("papelera.empty")}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {rows.map((row) => {
                const isOpen = expanded.has(row.id);
                const allFields = Object.entries(row)
                  .filter(([k]) => !HIDDEN_FIELDS.has(k))
                  .sort(([a], [b]) => a.localeCompare(b));
                return (
                  <div
                    key={row.id}
                    className={`min-w-0 rounded-xl border ${theme.cardBorder} ${theme.cardBg} p-4`}
                  >
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <i className={`${entity.icon} text-red-500`} />
                          <h3 className={`font-semibold truncate ${theme.text}`}>
                            {primaryTitle(
                              row,
                              entity.columns.map((c) => c.key),
                            )}
                          </h3>
                        </div>
                        <p className={`mt-0.5 font-mono text-[11px] ${theme.textSecondary}`}>
                          {row.id}
                        </p>
                      </div>
                      <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                        <i className="ri-delete-bin-line" />
                        {t("papelera.deletedBadge")}
                      </span>
                    </div>

                    {/* Quick fields */}
                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {entity.columns.map((col) => (
                        <div key={col.key} className="min-w-0">
                          <p className={`text-[11px] uppercase tracking-wide ${theme.textSecondary}`}>
                            {t(col.labelKey)}
                          </p>
                          <p className={`text-sm truncate ${theme.text}`}>
                            {formatValue(row[col.key])}
                          </p>
                        </div>
                      ))}
                    </div>

                    {/* All fields (collapsible) */}
                    {isOpen && (
                      <div className={`mt-3 pt-3 border-t ${theme.border} grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5`}>
                        {allFields.map(([key, value]) => (
                          <div key={key} className="min-w-0">
                            <p className={`text-[11px] uppercase tracking-wide ${theme.textSecondary}`}>
                              {key}
                            </p>
                            <p className={`text-sm break-words ${theme.text}`}>
                              {formatValue(value)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <button
                        onClick={() => toggleExpanded(row.id)}
                        className={`text-xs font-medium ${theme.textSecondary} hover:underline`}
                      >
                        <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />{" "}
                        {isOpen ? t("papelera.hideAll") : t("papelera.viewAll")}
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        {canManage && (
                          <>
                            {renderGuard(
                              <button
                                onClick={() => handleRestore(row)}
                                disabled={actionDisabled(row.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <i className="ri-arrow-go-back-line" />
                                {t("papelera.restore.button")}
                              </button>,
                            )}
                            {!entity.noHardDelete &&
                              renderGuard(
                                <button
                                  onClick={() => handleHardDelete(row)}
                                  disabled={actionDisabled(row.id)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <i className="ri-delete-bin-line" />
                                  {t("papelera.hardDelete.button")}
                                </button>,
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="mt-6">
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
